import React, { useEffect, useMemo, useState } from "react";
import { Plus, Loader2, Trash2, Play, Edit2, X, Power, History, Zap, Workflow } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "../../lib/ist";
import ModalPortal from "../../components/ModalPortal";

export default function AdminWorkflows() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // editing draft
  const [runs, setRuns] = useState(null);  // {workflow, rows}

  const load = async () => {
    setLoading(true);
    try {
      const [list, m] = await Promise.all([
        api.get("/workflows").then(r => r.data),
        api.get("/workflows/meta").then(r => r.data),
      ]);
      setItems(list); setMeta(m);
    } catch { toast.error("Failed to load workflows"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEdit({
      id: null, name: "", trigger: (meta?.triggers || ["customer.signup"])[0],
      enabled: true,
      actions: [{ type: "send_email", params: { to: "{user.email}", subject: "Hi {user.name}", body: "Thanks!" } }],
    });
  };

  const toggleEnabled = async (wf) => {
    try { await api.patch(`/workflows/${wf.id}`, { enabled: !wf.enabled }); load(); }
    catch { toast.error("Failed"); }
  };

  const remove = async (wf) => {
    if (!window.confirm(`Delete workflow "${wf.name}"?`)) return;
    try { await api.delete(`/workflows/${wf.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const test = async (wf) => {
    try {
      const sample = {
        user: { id: "demo-uid", email: "demo@optivize.digital", name: "Demo Test" },
        order: { id: "test-order-123", total_inr: 999 },
      };
      const { data } = await api.post(`/workflows/${wf.id}/test`, { payload: sample });
      toast.success(`Fired ${data.fired_workflows} workflow(s) · check Runs`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };

  const openRuns = async (wf) => {
    try {
      const { data } = await api.get(`/workflows/${wf.id}/runs?limit=30`);
      setRuns({ wf, rows: data });
    } catch { toast.error("Failed to load runs"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-workflows-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Workflow size={20} className="text-blue-400" /> Workflow Automation
          </h2>
          <p className="text-slate-400 text-sm">Trigger actions when events happen — like Zapier, built in.</p>
        </div>
        <button onClick={startCreate} className="btn-primary inline-flex items-center gap-2" data-testid="new-workflow-btn"><Plus size={14} /> New workflow</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-400" /></div>
      ) : items.length === 0 ? (
        <div className="card p-10 text-center">
          <Zap size={32} className="text-blue-400 mx-auto mb-3 opacity-60" />
          <h3 className="text-white font-semibold">No workflows yet</h3>
          <p className="text-slate-400 text-sm mt-1">Create one to automatically send emails, top up credits or call webhooks when events fire.</p>
          <button onClick={startCreate} className="btn-primary mt-4 inline-flex items-center gap-2" data-testid="empty-create-btn"><Plus size={14} /> Create your first workflow</button>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
              <tr>
                <th className="py-3 px-4 text-left">Name</th>
                <th className="text-left">Trigger</th>
                <th className="text-left">Actions</th>
                <th className="text-right">Runs</th>
                <th className="text-left pl-4">Last run</th>
                <th className="text-right pr-4">Controls</th>
              </tr>
            </thead>
            <tbody>
              {items.map(w => (
                <tr key={w.id} className="border-b border-slate-800" data-testid={`wf-row-${w.id}`}>
                  <td className="py-2.5 px-4 text-white font-medium">{w.name}</td>
                  <td className="text-cyan-300 font-mono text-xs">{w.trigger}</td>
                  <td className="text-slate-300 text-xs">{(w.actions || []).map(a => a.type).join(" → ")}</td>
                  <td className="text-right text-slate-400 font-mono">{w.run_count || 0}</td>
                  <td className="text-slate-400 pl-4 text-xs">{w.last_run_at ? fmtDateTime(w.last_run_at) : "—"}</td>
                  <td className="text-right pr-4">
                    <div className="inline-flex gap-1">
                      <button onClick={() => toggleEnabled(w)} title={w.enabled ? "Disable" : "Enable"}
                        className={`p-1.5 ${w.enabled ? "text-emerald-300 hover:text-emerald-200" : "text-slate-500 hover:text-slate-300"}`}
                        data-testid={`toggle-${w.id}`}><Power size={15} /></button>
                      <button onClick={() => test(w)} title="Test fire" className="text-blue-300 hover:text-blue-200 p-1.5" data-testid={`test-${w.id}`}><Play size={15} /></button>
                      <button onClick={() => openRuns(w)} title="Run history" className="text-slate-300 hover:text-white p-1.5" data-testid={`runs-${w.id}`}><History size={15} /></button>
                      <button onClick={() => setEdit({ ...w })} title="Edit" className="text-slate-300 hover:text-white p-1.5" data-testid={`edit-${w.id}`}><Edit2 size={15} /></button>
                      <button onClick={() => remove(w)} title="Delete" className="text-rose-300 hover:text-rose-200 p-1.5" data-testid={`del-${w.id}`}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {edit && meta && <EditModal draft={edit} meta={meta} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {runs && <RunsModal data={runs} onClose={() => setRuns(null)} />}
    </div>
  );
}

function EditModal({ draft, meta, onClose, onSaved }) {
  const [d, setD] = useState(draft);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (d.name.trim().length < 2) { toast.error("Name too short"); return; }
    setBusy(true);
    try {
      if (d.id) {
        await api.patch(`/workflows/${d.id}`, { name: d.name, trigger: d.trigger, enabled: d.enabled, actions: d.actions });
      } else {
        await api.post(`/workflows`, { name: d.name, trigger: d.trigger, enabled: d.enabled, actions: d.actions });
      }
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const addAction = () => setD({ ...d, actions: [...d.actions, { type: "notify_admin", params: { title: "[Workflow] {_event}" } }] });
  const removeAction = (i) => setD({ ...d, actions: d.actions.filter((_, idx) => idx !== i) });

  return (
    <ModalPortal><div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="wf-edit-modal">
      <div className="card max-w-2xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-white"><X size={18} /></button>
        <h3 className="text-white font-semibold text-lg mb-4" style={{ fontFamily: "Outfit" }}>{d.id ? "Edit" : "New"} workflow</h3>

        <div className="space-y-3">
          <div><label className="label">Name</label>
            <input className="input" value={d.name} onChange={e => setD({ ...d, name: e.target.value })} data-testid="wf-name" placeholder="e.g. Welcome bonus" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Trigger</label>
              <select className="input" value={d.trigger} onChange={e => setD({ ...d, trigger: e.target.value })} data-testid="wf-trigger">
                {meta.triggers.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={d.enabled} onChange={e => setD({ ...d, enabled: e.target.checked })} data-testid="wf-enabled" />
                Enabled
              </label>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-3 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-white font-semibold text-sm">Actions ({d.actions.length})</h4>
              <button onClick={addAction} className="btn-ghost !py-1 !px-3 text-xs inline-flex items-center gap-1" data-testid="add-action"><Plus size={12} /> Add action</button>
            </div>
            {d.actions.map((a, i) => (
              <div key={i} className="bg-slate-900/60 border border-slate-700 rounded-xl p-3 space-y-2" data-testid={`action-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 text-xs">Step {i + 1}</span>
                  <select
                    className="input !py-1 text-sm"
                    value={a.type}
                    onChange={e => {
                      const t = e.target.value;
                      const params = (meta.param_hints || {})[t] || {};
                      const updated = [...d.actions];
                      updated[i] = { type: t, params };
                      setD({ ...d, actions: updated });
                    }}
                    data-testid={`action-type-${i}`}
                  >
                    {meta.actions.map(t => <option key={t}>{t}</option>)}
                  </select>
                  <button onClick={() => removeAction(i)} className="ml-auto text-rose-300 hover:text-rose-200 p-1" data-testid={`rm-action-${i}`}><Trash2 size={13} /></button>
                </div>
                <textarea
                  rows="4" className="input font-mono text-xs"
                  value={JSON.stringify(a.params || {}, null, 2)}
                  onChange={e => {
                    try {
                      const params = JSON.parse(e.target.value);
                      const updated = [...d.actions];
                      updated[i] = { ...a, params };
                      setD({ ...d, actions: updated });
                    } catch { /* ignore malformed */ }
                  }}
                  data-testid={`action-params-${i}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost !py-2 !px-4">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="wf-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Save workflow
          </button>
        </div>
      </div>
    </div></ModalPortal>
  );
}

function RunsModal({ data, onClose }) {
  return (
    <ModalPortal><div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="runs-modal">
      <div className="card max-w-3xl w-full p-6 relative max-h-[80vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-white"><X size={18} /></button>
        <h3 className="text-white font-semibold text-lg" style={{ fontFamily: "Outfit" }}>Run history — {data.wf.name}</h3>
        <p className="text-slate-400 text-xs mt-1">Last {data.rows.length} executions</p>
        {data.rows.length === 0 ? (
          <div className="text-slate-500 text-sm py-10 text-center">No runs yet. Use the Test button on the list to fire a synthetic event.</div>
        ) : (
          <div className="mt-4 space-y-3">
            {data.rows.map(r => (
              <div key={r.id} className="bg-slate-900/60 border border-slate-700 rounded-xl p-3" data-testid={`run-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "ok" ? "bg-emerald-500/20 text-emerald-300" : r.status === "partial" ? "bg-amber-500/20 text-amber-300" : "bg-rose-500/20 text-rose-300"}`}>{r.status}</span>
                  <span className="text-slate-500 text-xs">{fmtDateTime(r.created_at)}</span>
                </div>
                <div className="text-cyan-300 font-mono text-xs mt-2">{r.event}</div>
                <div className="mt-2 space-y-1">
                  {(r.actions || []).map((a, i) => (
                    <div key={i} className="text-xs text-slate-300 flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${a.ok ? "bg-emerald-400" : "bg-rose-400"}`} />
                      <span className="text-slate-400">{a.type}</span>
                      {a.error && <span className="text-rose-300">— {a.error}</span>}
                      {a.balance != null && <span className="text-emerald-300">→ balance {a.balance}</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div></ModalPortal>
  );
}
